import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {TexturesApiService} from "@app/textures/service/textures-api.service"
import {EventEmitter} from "@angular/core"
import {PainterCacheHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/hal/painter-cache-hal"
import {
    AnyImageOpContext,
    AnyImageOpResultNode,
    ImageOpNode,
    ImageOpOutputs,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpNodeGraphEvaluator} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-node-graph-evaluator"
import {ImageOpContextImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-imgproc"
import {assertNever} from "@cm/utils"
import {ImageOpOutputsOfType, ImageOpType} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/specializing-image-op-node"
import {ImageOpDataObjectManager} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-data-object-manager"
import {HalContext} from "@cm/hal"
import {TextureEditorSettings} from "@app/textures/texture-editor/texture-editor-settings"
import {addRefImageOps} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/image-op-utils"
import {runWebGl2Tests} from "@cm/webgl2/test/index"
import {TextureTileLoader} from "@cm/three-texture-streaming-browser"

export class ImageOpSystem {
    readonly sourceImageChanged = new EventEmitter<AnyImageOpResultNode | null>()

    constructor(
        halContext: HalContext,
        readonly texturesApi: TexturesApiService,
        tileLoader: TextureTileLoader,
    ) {
        // TODO remove this
        if (TextureEditorSettings.EnableTests) {
            runWebGl2Tests(halContext)
            // testRegionMapping()
            // testRegionDependencyGraphOperations()
            // testExtractRegion()
            // testBlit()
        }

        this.contextImgProc = new ImageOpContextImgProc(texturesApi)
        this.painterCache = new PainterCacheHal(halContext)
        this.contextHal = new ImageOpContextHal(this.painterCache, halContext, texturesApi)
        this.dataObjectManager = new ImageOpDataObjectManager(this.contextHal, texturesApi, tileLoader)
        this.contextHal.dataObjectManager = this.dataObjectManager
    }

    dispose() {
        this.contextHal.dispose()
        this.contextImgProc.dispose()
        this.dataObjectManager.dispose()
        this.painterCache.dispose()
    }

    async evaluate<Type extends ImageOpType, ImageOpNodeType extends ImageOpNode>(
        type: Type,
        nodeToEvaluate: ImageOpNodeType,
    ): Promise<ImageOpOutputsOfType<ImageOpNodeType["definition"], Type>> {
        let context: AnyImageOpContext
        switch (type) {
            case "hal":
                context = this.contextHal
                break
            case "imgproc":
                context = this.contextImgProc
                break
            default:
                assertNever(type)
        }
        // evaluate
        const evaluator = new ImageOpNodeGraphEvaluator(context, this.texturesApi.logger)
        const evaluatedNode = (await evaluator.get(nodeToEvaluate)) as ImageOpOutputs<ImageOpNodeType["definition"]>
        addRefImageOps(evaluatedNode, REF_COUNT_DEBUG_ID_IMAGE_OP_SYSTEM_EVALUATE_RESULT)
        evaluator.cleanUpReferences()
        return evaluatedNode as ImageOpOutputsOfType<ImageOpNodeType["definition"], Type>
    }

    readonly painterCache: PainterCacheHal

    private dataObjectManager: ImageOpDataObjectManager
    private contextHal: ImageOpContextHal
    private contextImgProc: ImageOpContextImgProc
}

export const REF_COUNT_DEBUG_ID_IMAGE_OP_SYSTEM_EVALUATE_RESULT = "ImageOpSystem.evaluateResult"
